import { useTheme } from "styled-components";
import { useTranslation } from "react-i18next";
import { ChangeEvent, FormEvent, useContext, useRef } from "react";
import { DashValidation, isEmptyOrNil, isNameLengthValid } from "dash-common";
import {
  Form,
  FormErrorTypes,
  Modal,
  ModalContext,
  Text,
  ViewerContext,
  toast,
  useFormState,
  useFormValidation,
  useServerResponseTranslation,
} from "dash-ui";

import {
  CurrentBrokerHasPortfoliosDocument,
  GetAllPortfolioFoldersDocument,
  GetPortfoliosDocument,
  useCreatePortfolioFolderMutation,
} from "gql/generated.graphql";
import {
  createGTMEvent,
  getGoogleTagManagerUserId,
} from "common/google-tag-manager/helpers";
import { GTMEventsEnum } from "common/google-tag-manager/constants";

enum FormFields {
  FolderName = "folderName",
}

const AddPortfolioFolder = () => {
  const { account } = useContext(ViewerContext);
  const theme = useTheme();
  const ref = useRef<HTMLFormElement | null>(null);
  const { isOpenCurrent, toggleModal } = useContext(ModalContext);
  const [formState, [setFolderName]] = useFormState([FormFields.FolderName]);
  const [formErrors, , [setFolderError]] = useFormValidation([
    FormFields.FolderName,
  ]);
  const isOpen = isOpenCurrent["AddPortfolioFolder"];
  const { t } = useTranslation(["pages", "common"]);
  const translateErrorMessage = useServerResponseTranslation();

  const [createFolder, { loading }] = useCreatePortfolioFolderMutation();

  const onClose = () => {
    setFolderName("");
    toggleModal(false, "AddPortfolioFolder");
    setFolderError(null);
  };

  const handleFolderName = (e: ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setFolderName(value);
    if (isEmptyOrNil(value)) {
      return setFolderError({
        type: FormErrorTypes.Invalid,
        message: t("forms:messages.nameBlank"),
      });
    }
    if (!isNameLengthValid(value)) {
      return setFolderError({
        type: FormErrorTypes.Invalid,
        message: t("forms:messages.maxCharacterLimit", {
          number: DashValidation.MaxNameLength.toString(),
        }),
      });
    }

    if (value.includes("/")) {
      return setFolderError({
        type: FormErrorTypes.Invalid,
        message: t("forms:messages.invalidCharacters"),
      });
    }
    setFolderError(null);
  };

  const onSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    e.stopPropagation();

    if (isEmptyOrNil(formState.folderName)) {
      return setFolderError({
        type: FormErrorTypes.Invalid,
        message: t("forms:messages.nameBlank"),
      });
    }
    if (!isNameLengthValid(formState.folderName)) {
      return setFolderError({
        type: FormErrorTypes.Invalid,
        message: t("forms:messages.maxCharacterLimit", {
          number: DashValidation.MaxNameLength.toString(),
        }),
      });
    }

    if (formState.folderName.includes("/")) {
      return setFolderError({
        type: FormErrorTypes.Invalid,
        message: t("forms:messages.invalidCharacters"),
      });
    }

    createFolder({
      variables: {
        name: formState.folderName,
      },
      refetchQueries: [
        CurrentBrokerHasPortfoliosDocument,
        GetPortfoliosDocument,
        {
          query: GetAllPortfolioFoldersDocument,
          fetchPolicy: "network-only",
        },
      ],
      onCompleted: () => {
        toast.success(t("pages:portfoliosPage.toasts.folderIsCreated"));
        if (account) {
          createGTMEvent({
            dataLayer: {
              event: GTMEventsEnum.PORTFOLIO_CREATED,
              user_id: getGoogleTagManagerUserId(account.email),
            },
          });
        }
        onClose();
      },
      onError: (error) => toast.error(translateErrorMessage(error)),
    });
  };

  return (
    <Modal
      title={t("pages:portfoliosPage.createFolderModalTitle") || ""}
      isOpen={isOpen}
      submitButton={{
        title: t("common:labels.add"),
        disabled: !formState.folderName || loading,
        isLoading: loading,
        onClick: () => {
          if (ref.current) {
            ref.current.requestSubmit();
          }
        },
      }}
      cancelButton={{ title: t("common:labels.cancel"), onClick: onClose }}
      onRequestClose={onClose}
    >
      <Form ref={ref} onSubmit={onSubmit}>
        <Text color={theme.colors.n80}>
          {t("pages:portfoliosPage.createFolderModalSubtitle")}
        </Text>
        <Form.Group mt={3} mb={4}>
          <Form.Label required>
            {t("pages:portfoliosPage.portfolioName")}
          </Form.Label>
          <Form.Input
            w="100%"
            placeholder={
              t("pages:portfoliosPage.portfolioNamePlaceholder") || ""
            }
            onChange={handleFolderName}
            $errorType={formErrors[FormFields.FolderName]?.type}
          />
          <Form.Feedback $errorType={formErrors[FormFields.FolderName]?.type}>
            {formErrors[FormFields.FolderName]?.message}
          </Form.Feedback>
        </Form.Group>
      </Form>
    </Modal>
  );
};

export default AddPortfolioFolder;
